import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	camelToSnake,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Observable } from 'rxjs';

import { GridType } from '../../grid/domain/grid.domain';
import { DcBaseRequester } from '../core/dc-base.requester';
import { IGrid, IGridResponse } from './grid.model';

@Injectable({
	providedIn: 'root',
})
export class GridRequester extends DcBaseRequester<IGridResponse> {
	protected resource: string;
	protected elementsListUrl: string;
	protected singleElementUrl: string;

	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('GRIDS_GET_ALL');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getGridList(
		useCache = true
	): Observable<ReadonlyArray<IGridResponse>> {
		const resourceUrl = this.generateResourceUrl('GRIDS_GET_ALL_DETAILED');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<IGridResponse>>(url, cachePolicy);
	}

	public getGrid(gridId: number, type: GridType): Observable<IGrid> {
		const resourceUrl = this.generateResourceUrl(this.getApiKeyByType(type));
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: gridId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.get<IGrid>(url);
	}

	public changeActivationStatusGrid(grid: IGrid): Observable<void> {
		const resourceUrl = this.generateResourceUrl(
			this.getApiKeyByType(grid.type)
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: grid.id,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.put<void>(url, {
			metadata: {
				active: grid.metadata.actif,
				[camelToSnake('shortLabel')]: grid.metadata.label,
			},
		});
	}

	public deleteGrid(gridId: number, type: GridType): Observable<void> {
		const resourceUrl = this.generateResourceUrl(this.getApiKeyByType(type));
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: gridId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.delete<void>(url);
	}

	public deleteManyGrids(gridIds: Array<number>): Observable<void> {
		const resourceUrl = this.generateResourceUrl('GRIDS_DELETE_BULK');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				gridsId: gridIds,
			}
		);
		return this.http.delete<void>(url);
	}

	public duplicateGrid(gridId: number, type: GridType): Observable<number> {
		const resourceUrl = this.generateResourceUrl(
			this.getDuplicateApiKeyBYType(type)
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: gridId,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.post<number>(url, {});
	}

	private getApiKeyByType(type: GridType): string {
		if (type === 'pivot') {
			return 'GRIDS_PIVOT_GET';
		}
		if (type === 'simple') {
			return 'GRIDS_SIMPLE_GET';
		}
		throw new Error(`Unrecognized type: ${type}`);
	}

	private getDuplicateApiKeyBYType(type: GridType): string {
		if (type === 'pivot') {
			return 'GRIDS_PIVOT_DUPLICATE';
		}
		if (type === 'simple') {
			return 'GRIDS_SIMPLE_DUPLICATE';
		}
		throw new Error(`Unrecognized type: ${type}`);
	}
}
